import React, { useState } from 'react';
import { ArrowRight, Check, MessageCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { WHATSAPP_URL } from '../constants';
import { Reveal, SectionHeading } from './ui-blocks';

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL = { name: '', phone: '', email: '', transaction_type: 'buy', crypto: 'USDT', amount: '', message: '' };

const inputCls =
  'w-full bg-white border border-ink/15 focus:border-bordeaux focus:outline-none focus:ring-2 focus:ring-bordeaux/15 px-4 py-3.5 text-ink placeholder:text-ink/35 text-base transition-colors';
const labelCls = 'block text-xs font-bold uppercase tracking-[0.18em] text-ink/60 mb-2';

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
  </div>
);

const SuccessPanel = ({ t, reference, onReset }) => (
  <div className="h-full bg-ink text-cream p-8 md:p-12 flex flex-col justify-between gap-10" data-testid="request-success">
    <div>
      <span className="w-14 h-14 flex items-center justify-center bg-gold text-ink">
        <Check className="w-7 h-7" strokeWidth={2} />
      </span>
      <h3 className="mt-8 font-heading text-3xl md:text-4xl">{t.successTitle}</h3>
      <p className="mt-4 text-cream/70 leading-relaxed">{t.successText}</p>
      <p className="mt-4 font-heading text-2xl text-gold" data-testid="request-reference">{reference}</p>
    </div>
    <div className="flex flex-col sm:flex-row gap-4">
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="request-success-whatsapp"
        className="inline-flex items-center justify-center gap-3 rounded-full bg-gold hover:bg-gold-dark text-ink font-bold px-7 py-4"
      >
        <MessageCircle className="w-5 h-5" strokeWidth={2} />
        {t.successWhatsapp}
      </a>
      <button
        onClick={onReset}
        data-testid="request-reset-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/30 hover:border-cream text-cream font-semibold px-7 py-4"
      >
        <RotateCcw className="w-4 h-4" />
        {t.another}
      </button>
    </div>
  </div>
);

const TransactionRequest = () => {
  const { t } = useLanguage();
  const tr = t.request;
  const f = tr.fields;
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle');
  const [reference, setReference] = useState('');

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, message: form.message || null })
      });
      if (res.status === 429) {
        toast.error(tr.errorTitle, { description: tr.tooMany });
        setStatus('idle');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReference(data.id);
      setStatus('success');
      setForm(INITIAL);
      toast.success(tr.successTitle);
    } catch (err) {
      toast.error(tr.errorTitle, { description: tr.errorText });
      setStatus('idle');
    }
  };

  const reset = () => {
    setStatus('idle');
    setReference('');
  };

  return (
    <section id="request" data-testid="request-section" className="bg-cream py-24 md:py-32 border-t border-ink/10">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionHeading eyebrow={tr.eyebrow} title={tr.title} subtitle={tr.subtitle} testId="request-heading" />
            <Reveal delay={0.3}>
              <ol className="mt-12 border-t border-ink/10">
                {tr.steps.map((step, i) => (
                  <li key={step} className="flex items-center gap-5 py-5 border-b border-ink/10">
                    <span className="font-heading text-xl text-bordeaux">0{i + 1}</span>
                    <span className="text-ink/75">{step}</span>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15} className="h-full">
            {status === 'success' ? (
              <SuccessPanel t={tr} reference={reference} onReset={reset} />
            ) : (
              <form onSubmit={handleSubmit} data-testid="request-form" className="bg-white/60 border border-ink/10 p-6 md:p-10 flex flex-col gap-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field id="req-name" label={f.name}>
                    <input id="req-name" data-testid="request-name-input" className={inputCls} placeholder={f.namePh} value={form.name} onChange={update('name')} required minLength={2} maxLength={80} />
                  </Field>
                  <Field id="req-phone" label={f.phone}>
                    <input id="req-phone" data-testid="request-phone-input" type="tel" className={inputCls} placeholder={f.phonePh} value={form.phone} onChange={update('phone')} required minLength={7} maxLength={25} />
                  </Field>
                </div>

                <Field id="req-email" label={f.email}>
                  <input id="req-email" data-testid="request-email-input" type="email" className={inputCls} placeholder={f.emailPh} value={form.email} onChange={update('email')} required />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <span className={labelCls}>{f.type}</span>
                    <div className="grid grid-cols-2 border border-ink/15" role="radiogroup" data-testid="request-type-group">
                      {['buy', 'sell'].map((value) => (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={form.transaction_type === value}
                          data-testid={`request-type-${value}`}
                          onClick={() => setForm((p) => ({ ...p, transaction_type: value }))}
                          className={`py-3.5 text-sm font-bold uppercase tracking-[0.12em] ${
                            form.transaction_type === value ? 'bg-bordeaux text-cream' : 'bg-white text-ink/60 hover:text-bordeaux'
                          }`}
                        >
                          {f[value]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Field id="req-crypto" label={f.crypto}>
                    <select id="req-crypto" data-testid="request-crypto-select" className={`${inputCls} appearance-none`} value={form.crypto} onChange={update('crypto')}>
                      {Object.entries(f.cryptoOptions).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field id="req-amount" label={f.amount}>
                  <input id="req-amount" data-testid="request-amount-input" className={inputCls} placeholder={f.amountPh} value={form.amount} onChange={update('amount')} required maxLength={60} />
                </Field>

                <Field id="req-message" label={f.message}>
                  <textarea id="req-message" data-testid="request-message-input" rows={4} className={`${inputCls} resize-none`} placeholder={f.messagePh} value={form.message} onChange={update('message')} maxLength={1000} />
                </Field>

                <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-2">
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    data-testid="request-submit-btn"
                    className="group inline-flex items-center justify-center gap-3 rounded-full bg-bordeaux hover:bg-bordeaux-dark disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 text-cream font-bold px-8 py-4"
                  >
                    {status === 'sending' ? tr.sending : tr.submit}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="text-xs text-ink/50 leading-relaxed max-w-xs">{tr.privacy}</p>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default TransactionRequest;
